import FormattedMessage from 'I18n/components/FormattedMessage';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { HighlightedProductsHeaderConfig } from '../../../constants/merchCards/HighlightedProductsHeaderConfig';
import { MERCH_CARD } from '../../../constants/tracking/Locations';
import { useAppSelector } from '../../../reducers';
import { hasPricingAndPurchasingSucceededSelector, hasPurchaseMotionsFailedSelector, isMerchCardCTAsDataLoadingSelector } from '../../../selectors/LoadingStateSelectors';
import { hasProductVersionInProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { productSelector } from '../../../selectors/ProductsSelectors';
import { usePricingLibSelector } from '../../../selectors/SelectorUtils';
import { shouldShowTermSelectorForMerchSelector } from '../../../selectors/TermSelectors';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtils';
import CurrentPlanTag from '../../common/CurrentPlanTag';
import DetailedTermSelector from '../../common/DetailedTermSelector';
import ProductIcon from '../../common/ProductIcon';
import { Section, SectionTitle } from '../../common/SharedComponents';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import FallbackMerchCardCTAsContainer from '../../merch-card/FallbackMerchCardCTAsContainer';
import MerchCardCTAsContainer from '../../merch-card/MerchCardCTAsContainer';
import { borderStyle } from '../../merch-card/MerchCardSharedComponents';
import BusinessObjectivesContainer from '../../merch-card/business-objectives/BusinessObjectivesContainer';
import HighlightedMerchPriceDisplay from '../../merch-card/price-display/HighlightedMerchPriceDisplay';
import { useSkuAdditionalUnits } from '../../merch-card/useSkuAdditionalUnits';
import AdditionalUnitsPicker from '../../plan-builder/AdditionalUnitsPicker';
import RequiredOnboardingSubtextContainer from '../../plan-builder/RequiredOnboardingSubtextContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledTermSelectorContainer = styled(DetailedTermSelector).withConfig({
  displayName: "MobileHighlightedMerchCard__StyledTermSelectorContainer",
  componentId: "nb7o5b-0"
})(["max-width:300px;margin:0 auto;padding:16px 0;"]);
const MobileHighlightedMerchCard = ({
  productLevel,
  productLine,
  commonStyles,
  selectedTab
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isMerchCardCTAsDataLoading = useSelector(isMerchCardCTAsDataLoadingSelector);
  const hasPurchaseMotionsFailed = useSelector(hasPurchaseMotionsFailedSelector);
  const product = useSelector(state => productSelector(state, {
    productLine,
    productLevel
  }));
  const apiName = product && product.apiName;
  const seleniumId = apiName || 'loading';
  const {
    additionalUnitSkuId,
    isAdditionalUnitSkuIdAQuantityPack,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    skuId
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const ownsProductVersion = useAppSelector(state => hasPricingAndPurchasingSucceeded && hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    title = '',
    subtitle = ''
  } = HighlightedProductsHeaderConfig[productLine][productLevel] || {};
  const shouldShowTermSelector = useSelector(state => skuId && shouldShowTermSelectorForMerchSelector(state, {
    skuId
  }));
  const additionalUnitsPickerProps = {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    productConfigurations: [createProductConfig({
      skuId: skuId || 0,
      quantity: 1
    })],
    compact: true,
    queryParamTerm,
    trackingSource: MERCH_CARD,
    productLevel
  };

  // In order for the grid to work, we need this to remain enclosed in a Fragment and not a div.
  // Children of the display grid item must get the grid-start attributes directly
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Section, {
      className: "p-top-6",
      padding: "none",
      style: Object.assign({
        borderTop: borderStyle,
        position: 'relative',
        gridRowStart: 1
      }, commonStyles),
      "data-selenium-id": `${seleniumId}-merch-card`,
      children: [ownsProductVersion && /*#__PURE__*/_jsx(CurrentPlanTag, {}), /*#__PURE__*/_jsxs("div", {
        className: "display-flex align-center",
        children: [/*#__PURE__*/_jsx(ProductIcon, {
          className: "m-right-3",
          productLine: productLine,
          productLevel: productLevel
        }), /*#__PURE__*/_jsx(SectionTitle, {
          className: "m-bottom-0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: title
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(Section, {
      padding: "none",
      className: "p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 2,
        position: 'relative'
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitle
      })
    }), /*#__PURE__*/_jsx(Section, {
      padding: "none",
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 3
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [20, 33, 20],
        widths: [60, 80, 140],
        className: "flex-column",
        Content: HighlightedMerchPriceDisplay,
        contentProps: {
          queryParamTerm,
          skuId,
          useLargePrice: true,
          quantityPackSkuId: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitSkuId : null,
          quantityPackQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0
        }
      })
    }), !isSeatsRedesignUngated && /*#__PURE__*/_jsx(Section, {
      padding: "none",
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 4
      }),
      children: shouldShowTermSelector && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [41],
        widths: [300],
        className: "flex-column align-center",
        Content: StyledTermSelectorContainer,
        contentProps: {
          skuIds: isAdditionalUnitSkuIdAQuantityPack && additionalUnitQuantity > 0 ? [skuId, additionalUnitSkuId] : [skuId],
          onChange: setQueryParamTerm,
          activeTerm: queryParamTerm,
          trackingSource: MERCH_CARD
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      padding: "none",
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 5
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        className: "flex-column align-start",
        heights: [34, 76],
        widths: [226, 320],
        Content: AdditionalUnitsPicker,
        contentProps: additionalUnitsPickerProps
      })
    }), /*#__PURE__*/_jsx(Section, {
      padding: "none",
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 6
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isMerchCardCTAsDataLoading,
        isError: hasPurchaseMotionsFailed,
        heights: [47],
        Content: MerchCardCTAsContainer,
        contentProps: {
          queryParamTerm,
          skuId,
          productLine,
          additionalUnitSkuId,
          additionalUnitQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0,
          ctaStyles: {
            width: '100%',
            lineHeight: '23px'
          }
        },
        ErrorContent: FallbackMerchCardCTAsContainer,
        errorContentProps: {
          skuId
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      padding: "none",
      className: "p-y-5",
      style: Object.assign({
        gridRowStart: 8
      }, commonStyles),
      children: /*#__PURE__*/_jsx(UIAccordionItem, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "merchCards.businessObjectives.seeDetails"
        }),
        children: /*#__PURE__*/_jsx(BusinessObjectivesContainer, {
          productLine: productLine,
          productLevel: productLevel,
          apiName: apiName
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      padding: "none",
      className: "flex-column align-center p-bottom-6 p-top-3",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 10,
        borderBottom: borderStyle
      }),
      children: skuId && /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
        queryParamTerm: queryParamTerm,
        skuIds: [skuId],
        tab: selectedTab,
        trackingSource: MERCH_CARD,
        includesOnboardingCost: false
      })
    })]
  });
};
export default MobileHighlightedMerchCard;